import type { SupabaseClient } from "@supabase/supabase-js";

/**
 * Coach notification preferences — surfaced on the Billing page so
 * the coach can turn off the reply-email pulse without hunting for a
 * separate settings screen. Row is created on first toggle (upsert);
 * missing row defaults to opt-in everywhere downstream.
 */

export interface CoachNotificationPreferences {
  userId: string;
  emailOnStudentReply: boolean;
  dedupeWindowSeconds: number;
  updatedAt: string | null;
}

export const DEFAULT_COACH_NOTIFICATION_PREFERENCES: Omit<
  CoachNotificationPreferences,
  "userId" | "updatedAt"
> = {
  emailOnStudentReply: true,
  dedupeWindowSeconds: 600,
};

export async function getCoachNotificationPreferences(
  client: SupabaseClient,
): Promise<CoachNotificationPreferences | null> {
  const { data, error } = await client
    .from("coach_notification_preferences")
    .select(
      "user_id, email_on_student_reply, dedupe_window_seconds, updated_at",
    )
    .maybeSingle();
  if (error) throw error;
  if (!data) return null;
  return {
    userId: String(data.user_id),
    emailOnStudentReply: Boolean(data.email_on_student_reply ?? true),
    dedupeWindowSeconds: Number(data.dedupe_window_seconds ?? 600),
    updatedAt: (data.updated_at as string | null) ?? null,
  };
}

export async function upsertCoachNotificationPreferences(
  client: SupabaseClient,
  params: {
    userId: string;
    emailOnStudentReply?: boolean;
    dedupeWindowSeconds?: number;
  },
): Promise<CoachNotificationPreferences> {
  const payload: Record<string, unknown> = { user_id: params.userId };
  if (params.emailOnStudentReply !== undefined) {
    payload.email_on_student_reply = params.emailOnStudentReply;
  }
  if (params.dedupeWindowSeconds !== undefined) {
    payload.dedupe_window_seconds = params.dedupeWindowSeconds;
  }
  const { data, error } = await client
    .from("coach_notification_preferences")
    .upsert(payload, { onConflict: "user_id" })
    .select(
      "user_id, email_on_student_reply, dedupe_window_seconds, updated_at",
    )
    .single();
  if (error) throw error;
  return {
    userId: String(data.user_id),
    emailOnStudentReply: Boolean(data.email_on_student_reply ?? true),
    dedupeWindowSeconds: Number(data.dedupe_window_seconds ?? 600),
    updatedAt: (data.updated_at as string | null) ?? null,
  };
}

/** Resolve the effective prefs — applies defaults when the row is missing. */
export function resolveCoachNotificationPreferences(
  row: CoachNotificationPreferences | null,
  userId: string,
): CoachNotificationPreferences {
  if (row) return row;
  return {
    userId,
    emailOnStudentReply: DEFAULT_COACH_NOTIFICATION_PREFERENCES.emailOnStudentReply,
    dedupeWindowSeconds: DEFAULT_COACH_NOTIFICATION_PREFERENCES.dedupeWindowSeconds,
    updatedAt: null,
  };
}
